import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useErrorManager from '@/composable/useErrorManager'
import { ref } from '@vue/composition-api'
import useEntityHelper from '@/composable/useEntityHelper'

export default function useArticlesList() {
  function convertResource(resource, selectedLang) {
    return {
      ...resource,
      title: resource.title[selectedLang],
      keywords: resource.keywords ? resource.keywords[selectedLang] : '',
      description: resource.description ? resource.description[selectedLang] : '',
      answer: resource.answer[selectedLang],
      question: resource.question[selectedLang],
      tags: resource.tagIds && resource.tagIds.length ? resource.tagIds.filter(itemEntry => itemEntry && itemEntry.language === selectedLang) : null,
      approver: resource.approverId,
      editor: resource.editorId,
      user: resource.userId,
      writer: resource.writerId,
      relatedArticles: resource.relatedArticleIds && resource.relatedArticleIds.length
        ? resource.relatedArticleIds.map(itemEntry2 => ({
          _id: itemEntry2._id,
          title: itemEntry2.title ? itemEntry2.title[selectedLang] : '',
          articleThumbnailFileId: itemEntry2.articleThumbnailFileId,
        })) : [],
      category: resource.articleCategoryId ? { _id: resource.articleCategoryId._id, title: resource.articleCategoryId.title[selectedLang] } : null,
      lang: selectedLang,
      questionLink: { name: 'pages-questions-edit', params: { id: resource._id, lang: selectedLang } },
    }
  }

  const MODULE_NAME = 'questions'
  const ENDPOINT = 'questions'
  const errorManager = useErrorManager()
  const { createTagIdArray } = useEntityHelper()

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const {
    itemLoading, item, form, currentLanguage,
  } = entity
  const categoryOptions = ref([])
  const approverOptions = ref([])
  const isUniqueSlug = ref(true)
  const articleOptions = ref([])

  // Table Handlers
  const tableColumns = [
    {
      key: 'title', label: i18n.t('general.title'), type: 'truncate-text', linkKey: 'questionLink', sortable: true,
    },
    {
      key: 'question', label: i18n.t('general.question'), type: 'truncate-text', icon: 'HelpCircleIcon',
    },
    {
      key: 'category', label: i18n.t('general.category'), type: 'truncate-text', textSubKey: 'title', icon: 'GridIcon',
    },
    {
      key: 'approver', label: i18n.t('general.approver'), type: 'manager', icon: 'UserCheckIcon', lang: currentLanguage.value,
    },
    { key: 'user', label: i18n.t('general.creator'), type: 'user' },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ]

  const fetchCategories = (lang = 'fa', search = '', searchBy = '', parentId = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, parentId, limit: 20,
        },
        endpoint: 'articles-categories',
      })
      .then(response => {
        categoryOptions.value = response.data.map(itemEntry3 => ({ _id: itemEntry3._id, title: itemEntry3.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchApprovers = (lang = 'fa', search = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang, search },
        endpoint: 'managers',
      })
      .then(response => {
        approverOptions.value = response.data.data.map(itemEntry4 => ({
          _id: itemEntry4._id,
          firstName: itemEntry4.firstName[lang],
          lastName: itemEntry4.lastName[lang],
          managerImageFileId: itemEntry4.managerImageFileId,
        }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const checkSlugIsUnique = (lang = 'fa', slug) => {
    if (slug === '' || (item.value && item.value.slug === form.value.slug)) {
      isUniqueSlug.value = true
      return
    }
    itemLoading.value = true
    store
      .dispatch('crud/fetchItem',
        {
          data: { id: slug },
          queryParams: { lang },
          endpoint: 'articles/slug',
        })
      .then(response => {
        isUniqueSlug.value = response.data.isUnique
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchArticles = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, type: 'article',
        },
        endpoint: 'articles/relatedArticles/',
      })
      .then(response => {
        articleOptions.value = response.data.data.map(itemEntry5 => ({ _id: itemEntry5._id, title: itemEntry5.title[lang], articleThumbnailFileId: itemEntry5.articleThumbnailFileId }))
      })
      .catch(err => {
        useErrorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    categoryOptions,
    articleOptions,
    errorManager,
    fetchApprovers,
    approverOptions,
    convertResource,
    createTagIdArray,
    checkSlugIsUnique,
    fetchCategories,
    fetchArticles,
    tableColumns,
    isUniqueSlug,
  }
}
